import { Component } from '@theme/component';

// Klaviyo's legacy onsite endpoint (/onsite/components/back-in-stock/subscribe) was
// retired (returns 410 + no CORS). These are the supported client-side JSON:API
// endpoints, authenticated with the public key via the company_id query param.
const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_SUBSCRIBE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
// Date-versioned API revision. Bump to a newer stable revision if Klaviyo sunsets this one.
const KLAVIYO_REVISION = '2025-07-15';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Captures an email on a sold-out product and registers a Klaviyo back-in-stock
 * subscription via the onsite endpoint. When the "Hive" newsletter box is ticked
 * it also subscribes the email to the configured Klaviyo list.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} email - Email input.
 * @property {HTMLInputElement} [hive] - Hive newsletter opt-in checkbox.
 * @property {HTMLButtonElement} submit - Submit button.
 * @property {HTMLElement} fields - Wrapper around the form fields, hidden on success.
 * @property {HTMLElement} message - Status/error text region.
 *
 * @extends Component<Refs>
 */
class BackInStockForm extends Component {
  requiredRefs = ['email', 'submit', 'message'];

  connectedCallback() {
    super.connectedCallback();

    if (!this.publicKey) {
      console.warn('[back-in-stock] Missing Klaviyo public key — set it in theme settings.');
    }

    this.#maybeAutoOpen();
  }

  /**
   * Open the dialog automatically when the shopper arrived from a PLP / quick-view
   * "Notify me when back in stock" link (?notify_me=1). The flag is stripped from the
   * URL afterwards so a refresh doesn't reopen it.
   */
  #maybeAutoOpen() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('notify_me') !== '1') return;

    // Strip the flag up-front so a refresh / back-navigation won't re-open the dialog.
    params.delete('notify_me');
    const query = params.toString();
    const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', url);

    const open = () => {
      // Prefer the dialog we're nested inside; fall back to looking it up by id in
      // case a load-time section re-render replaced our original ancestor.
      const dialog =
        this.closest('dialog-component') ??
        (this.dataset.sectionId ? document.getElementById(`NotifyMeDialog-${this.dataset.sectionId}`) : null);

      if (dialog && typeof dialog.showDialog === 'function') {
        dialog.showDialog();
      } else {
        console.warn('[back-in-stock] auto-open requested but the notify dialog was not ready.', { dialog });
      }
    };

    // Wait for the dialog component to be defined, then defer one frame so any
    // load-time variant/section updates settle before we open (otherwise a morph
    // could immediately clobber the freshly opened dialog).
    customElements.whenDefined('dialog-component').then(() => requestAnimationFrame(open));
  }

  get publicKey() {
    return this.dataset.klaviyoPublicKey?.trim() ?? '';
  }

  get hiveListId() {
    return this.dataset.hiveListId?.trim() ?? '';
  }

  /**
   * Resolve the currently selected variant id. The dialog lives just outside the
   * <product-form-component>, so read the hidden variant input that product-form.js
   * keeps in sync on every variant change. Falls back to the server-rendered id.
   *
   * @returns {string}
   */
  get variantId() {
    const sectionId = this.dataset.sectionId;
    const input = sectionId
      ? document.querySelector(`product-form-component[data-section-id="${sectionId}"] [ref="variantId"]`)
      : null;

    return input?.value?.trim() || (this.dataset.defaultVariantId ?? '');
  }

  /**
   * Show a status message in the dialog.
   *
   * @param {string} key - dataset key holding the translated text (e.g. 'textError').
   * @param {{ isError?: boolean }} [options]
   */
  #showMessage(key, { isError = false } = {}) {
    const text = this.dataset[key];
    if (!text) return;
    this.refs.message.textContent = text;
    this.refs.message.classList.toggle('text-error', isError);
    this.refs.message.classList.toggle('text-black', !isError);
    this.refs.message.hidden = false;
  }

  /**
   * Clear the invalid-email state once the shopper edits the field.
   */
  clearError = () => {
    this.refs.email.removeAttribute('aria-invalid');
    if (this.refs.message.classList.contains('text-error')) {
      this.refs.message.hidden = true;
    }
  };

  #setLoading(isLoading) {
    const { submit } = this.refs;
    submit.disabled = isLoading;
    submit.textContent = isLoading ? this.dataset.textSending ?? '' : this.#defaultButtonLabel;
  }

  handleSubmit = async () => {
    // Validate first so the shopper always gets feedback, even if the Klaviyo key
    // isn't configured yet.
    const email = this.refs.email.value.trim();

    if (!EMAIL_PATTERN.test(email)) {
      this.refs.email.setAttribute('aria-invalid', 'true');
      this.#showMessage('textInvalidEmail', { isError: true });
      this.refs.email.focus();
      return;
    }

    this.refs.email.removeAttribute('aria-invalid');

    if (!this.publicKey) {
      console.warn('[back-in-stock] Missing Klaviyo public key — set it in theme settings.');
      this.#showMessage('textError', { isError: true });
      return;
    }

    const variantId = this.variantId;
    if (!variantId) {
      this.#showMessage('textError', { isError: true });
      return;
    }

    // Remember the original label so we can restore it after a loading state.
    this.#defaultButtonLabel ??= this.refs.submit.textContent ?? '';

    this.refs.message.hidden = true;
    this.#setLoading(true);

    try {
      const response = await fetch(`${KLAVIYO_BIS_ENDPOINT}?company_id=${encodeURIComponent(this.publicKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  // Shopify-synced catalog variant external id.
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      });

      if (!response.ok) throw new Error(`Klaviyo BIS responded ${response.status}`);

      // Hive newsletter opt-in is best-effort — a failure here must not block the
      // primary back-in-stock confirmation.
      if (this.refs.hive?.checked && this.hiveListId) {
        this.#subscribeToHive(email).catch((error) => {
          console.warn('[back-in-stock] Hive list subscribe failed:', error);
        });
      }

      this.#onSuccess();
    } catch (error) {
      console.error('[back-in-stock] subscription failed:', error);
      this.#setLoading(false);
      this.#showMessage('textError', { isError: true });
    }
  };

  /**
   * @param {string} email
   * @returns {Promise<Response>}
   */
  #subscribeToHive(email) {
    return fetch(`${KLAVIYO_SUBSCRIBE_ENDPOINT}?company_id=${encodeURIComponent(this.publicKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock form',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.hiveListId } },
          },
        },
      }),
    });
  }

  #onSuccess() {
    if (this.refs.fields) this.refs.fields.hidden = true;
    this.refs.submit.hidden = true;
    this.#showMessage('textSuccess');
  }

  /** @type {string | undefined} */
  #defaultButtonLabel;
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
